import React, { useRef, useState, useEffect } from 'react';
import styles from './AsnUpdatesLive.module.css';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { buildErrorMessage } from '../../apis/calls';
import {
  AGGREGATES,
  AGGREGATE_STATUS,
  API_RESOURCE_URLS,
  DATE_FORMAT,
  SCREEN_REFRESH_INTERVAL_TIME_MS,
  PROCUREMENT_TYPE_SPCL,
} from '../../constants';
import { API } from '../../apis/api';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import AsnUpdatesLiveToolbar from '../AsnUpdatesLiveToolbar/AsnUpdatesLiveToolbar';
import AsnUpdatesTable from '../AsnUpdatesTable/AsnUpdatesTable';
import AsnUpdateFooter from '../AsnUpdateFooter/AsnUpdateFooter';
import {
  formatDataToIncludeColour,
  getAggregateStatusAndModifiedAtAndComment,
} from '../../utils/tableUtils';
import moment from 'moment';

const AsnUpdatesLive = () => {
  const { showInternalError,showFailure } = usePopupManager();
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;
  const [selectedLine, setSelectedLine] = useState([]);
  const [activeAsnDate, setActiveAsnDate] = useState(null);
  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState([]);

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
  });
  const [countData, setCountData] = useState({
    loading: false,
    data: [],
  });

  if (selectedLine === '') {
    setSelectedLine(line);
  }
  const statusFieldByMaterialGroup = new Map([
    [AGGREGATES.FRAME, 'frameStatuses'],
    [AGGREGATES.FRONT_AXLE, 'frontAxleStatuses'],
    [AGGREGATES.REAR_AXLE, 'rearAxleStatuses'],
    [AGGREGATES.CAB, 'cabStatuses'],
    [AGGREGATES.ENGINE, 'engineStatuses'],
    [AGGREGATES.MATERIAL, 'scmMaterialStatuses'],
    [AGGREGATES.PAINT, 'paintStatuses'],
    [AGGREGATES.CYLINDER_HEAD, 'cylinderHeadStatuses'],
    [AGGREGATES.CYLINDER_BLOCK, 'cylinderBlockStatuses'],
    [AGGREGATES.CAMSHAFT, 'camshaftStatuses'],
    [AGGREGATES.CRANKSHAFT, 'crankshaftStatuses'],
  ]);

  const addAggregateStatusFlagsAndSortAndFormatForRender = asns => {
    if (asns.length === 0) return [];
    const rows = asns.map(asn => {
      const updatedMaterialGroupClusters = asn.materialGroupClusters.map(({ name, materials }) => {
        // Check if the section name is 'Engine', 'Frame', or 'Cab'
        if (name === AGGREGATES.FRAME || name === AGGREGATES.ENGINE || name === AGGREGATES.CAB) {
          let selectedMaterial;
          const readyForPickupMaterial = materials.find(material => material.aggregateStatus === 'READY_FOR_PICKUP');
          if (readyForPickupMaterial) {
            selectedMaterial = readyForPickupMaterial; // Select the material with READY_FOR_DROP status
          } else {
            // Otherwise, check if any material has `aggregateProcurementSpecialType` as '99' or '98'
            const specialMaterial = materials.find(material =>
              material.aggregateProcurementSpecialType === PROCUREMENT_TYPE_SPCL.E99 ||
              material.aggregateProcurementSpecialType === PROCUREMENT_TYPE_SPCL.E98
            );

            if (specialMaterial) {
              selectedMaterial = specialMaterial;
            }
          }
          if (selectedMaterial) {
            return {
              name,
              materials: [getAggregateStatusAndModifiedAtAndComment(selectedMaterial)], // Map only the selected material
            };
          }
        }
        return {
          name,
          materials: materials.map(material =>
            getAggregateStatusAndModifiedAtAndComment(material),
          ),
        };
      });
      const hasUnAvailableAggregates = updatedMaterialGroupClusters
        .map(({ materials }) => materials[0])
        .some(
          ({ aggregateStatus }) =>
            aggregateStatus === AGGREGATE_STATUS.LINE_OUT,
        );
      const aggregateStatuses = updatedMaterialGroupClusters.reduce(
        (acc, materialGroup) => {
          const aggregateStatus = materialGroup.materials.map(
            ({
              aggregateStatus,
              material,
              aggregateAgsnId,
              aggregatePlanningReason,
              aggregateSerialNumber,
              materialGroup,
              aggregateProcurementSpecialType,
              aggregateAgsn,
            }) => ({
              status: aggregateStatus,
              material,
              aggregateAgsnId,
              aggregatePlanningReason,
              aggregateSerialNumber,
              materialGroup,
              procurementTypeSpcl: aggregateProcurementSpecialType,
              aggregateAgsn,
            }),
          );
          const obj = {};
          if (statusFieldByMaterialGroup.has(materialGroup.name)) {
            // enabling multiple quantities data for front and rear axle
            if (
              materialGroup.name === AGGREGATES.FRONT_AXLE ||
              materialGroup.name === AGGREGATES.REAR_AXLE
            )
              obj[
                statusFieldByMaterialGroup.get(materialGroup.name)
              ] = aggregateStatus;
            else if (
              materialGroup.name === AGGREGATES.FRAME ||
              materialGroup.name === AGGREGATES.CAB ||
              materialGroup.name === AGGREGATES.ENGINE ||
              materialGroup.name === AGGREGATES.MATERIAL
            ) {
                materialGroup.materials.forEach(material => {
                  const availableMaterials = materialGroup.materials.find(
                    material => material?.status === 'READY_FOR_PICKUP'|| material?.status === 'BOOKED'
                  );
                  if(availableMaterials){
                    const latestStatus = availableMaterials;
                    obj[statusFieldByMaterialGroup.get(materialGroup.name)] = [latestStatus];
                  }
               else {
                    const foundProcurementType = aggregateStatus?.find(
                      procurementType =>
                        procurementType?.procurementTypeSpcl === PROCUREMENT_TYPE_SPCL.E99 ||
                        procurementType?.procurementTypeSpcl === PROCUREMENT_TYPE_SPCL.E98
                    );
                    // Only set the status field if a procurementType is found
                    if (foundProcurementType) {
                      const statusField = statusFieldByMaterialGroup.get(materialGroup.name);
                      if (statusField) {
                        obj[statusField] = [foundProcurementType];
                      }
                    }
                  }
                });
              }
               else
              obj[statusFieldByMaterialGroup.get(materialGroup.name)] = [
                aggregateStatus[0],
              ];
          }
          return { ...acc, ...obj };
        },
        {},
      );
      return {
        ...asn,
        ...aggregateStatuses,
        materialGroupClusters: [...updatedMaterialGroupClusters],
        hasUnAvailableAggregates,
      };
    });
    rows.sort((a, b) => a.asnId.localeCompare(b.asnId));
    const rowsWithIndex = rows.map((record, index) => {
      return { rowIndex: index + 1, ...record };
    });
    return rowsWithIndex;
  };

  const loadVcAsnForDateAndPlantAndLine = async (
    date = activeAsnDate,
    selectedPlantValue = plant.value,
    selectedChassisLineValue = line.value,
  ) => {
    if (!date)
      { setRowsData({
        loading: false,
        data: [],
        filteredData: [],
      });
      setCountData(
        {
          loading: false,
          data: [],
        }
      )
      return;}
    try {
      setRowsData(prevState => ({ ...prevState, loading: true }));
      const dateString = date.format(DATE_FORMAT.ISO);
      const currentdateString = moment().format(DATE_FORMAT.ISO);

      const chassisAsnsForADay = await API.get(
        API_RESOURCE_URLS.chassisAsnOverView(
          selectedPlantValue,
          selectedChassisLineValue,
          dateString,
        ),
      );
      const chassisAsnCount = await API.get(
        API_RESOURCE_URLS.chassisAsnCountSummary(
          selectedPlantValue,
          selectedChassisLineValue,
          dateString
        )
      );
      setCountData({
        loading: false,
        data: chassisAsnCount.data,
      });
      let filteredData = chassisAsnsForADay.data;
      if (dateString < currentdateString) {
        filteredData = chassisAsnsForADay.data;
      }
      let chassisOverViewWithChildMaterialStatus = [];
      let outerLoopCount = 0;
      const maxPayloadLength = 250;
      for (
        let outLoop = 0;
        outLoop < Math.ceil(filteredData?.length / maxPayloadLength);
        outLoop++
      ) {
        let innerLoopPayloadLenght = 0;

        if (outLoop > 0) {
          innerLoopPayloadLenght = outLoop * maxPayloadLength;
        }

        let asnIds = [];

        for (
          let asnCount = innerLoopPayloadLenght;
          asnCount < filteredData.length;
          asnCount++
        ) {
          if (outerLoopCount < 250) {
            if (asnIds.length <= 0)
              asnIds.push(filteredData[asnCount].asnId);
            else asnIds.push(filteredData[asnCount].asnId);
            outerLoopCount++;
          } else if (outLoop > 0) {
            if (
              outerLoopCount <
              maxPayloadLength * outLoop + maxPayloadLength
            ) {
              if (asnIds.length <= 0)
                asnIds.push(filteredData[asnCount].asnId);
              else asnIds.push(filteredData[asnCount].asnId);
              outerLoopCount++;
            }
          } else {
            outerLoopCount = 0;
            break;
          }
        }

        const childMaterialStatusesForADay = await API.get(
          API_RESOURCE_URLS.childMaterialStatus(
            selectedPlantValue,
            selectedChassisLineValue,
            asnIds,
          ),
        );

        for (
          let childMaterialCount = innerLoopPayloadLenght;
          childMaterialCount < filteredData.length;
          childMaterialCount++
        ) {
          if (childMaterialCount < 250) {
            const findChildMaterialAsn = childMaterialStatusesForADay.data.find(
              childMaterialDetails => {
                return (
                  filteredData[childMaterialCount].asnId ===
                  childMaterialDetails.asnId
                );
              },
            );

            let parentChildData = {
              ...filteredData[childMaterialCount],
              materialGroupClusters:
                findChildMaterialAsn?.materialGroupClusters || [],
            };

            chassisOverViewWithChildMaterialStatus.push(parentChildData);
          }

          if (
            outLoop > 0 &&
            childMaterialCount < maxPayloadLength * outLoop + maxPayloadLength
          ) {
            const findChildMaterialAsn = childMaterialStatusesForADay.data.find(
              childMaterialDetails => {
                return (
                  filteredData[childMaterialCount].asnId ===
                  childMaterialDetails.asnId
                );
              },
            );

            let parentChildData = {
              ...filteredData[childMaterialCount],
              materialGroupClusters:
                findChildMaterialAsn?.materialGroupClusters || [],
            };

            chassisOverViewWithChildMaterialStatus.push(parentChildData);
          }
        }
      }
      const processedData = addAggregateStatusFlagsAndSortAndFormatForRender(
        chassisOverViewWithChildMaterialStatus,
      );
      const data = formatDataToIncludeColour(processedData);
      setRowsData({
        loading: false,
        data,
      });
    } catch (err) {
      if (err?.response?.status === 400) {
        showFailure({});
      } else {
        console.error(buildErrorMessage(err));
        showInternalError({ error: err });
      }
      setRowsData(prevState => ({ ...prevState, loading: false }));
      setCountData(prevState => ({ ...prevState, loading: false }));
    }
  };

  useEffect(() => {
    loadVcAsnForDateAndPlantAndLine(activeAsnDate, plant.value, line.value);

    // Set up interval to refresh data every 5 seconds
    const intervalId = setInterval(
      loadVcAsnForDateAndPlantAndLine,
      SCREEN_REFRESH_INTERVAL_TIME_MS,
    );

    // Clean up the interval on component unmount
    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAsnDate, plant.value, selectedLine]);

  return (
    <div className={styles.display}>
      <div className={styles.header}>
        <AsnUpdatesLiveToolbar
          activeDate={activeAsnDate}
          setActiveDate={setActiveAsnDate}
          plant={plant}
          line={line}
          selectedOptionalColNames={selectedOptionalColNames}
          handleColumnSelection={selection =>
            setSelectedOptionalColNames(selection)
          }
        />
      </div>
      <div className={styles.tableArea}>
        <AsnUpdatesTable
          rows={rowsData.data}
          selectedOptionalColNames={selectedOptionalColNames}
        />
      </div>
      <div className={styles.footer}>
        <AsnUpdateFooter asns={rowsData.data} activeDate={activeAsnDate} countData={countData} />
      </div>
    </div>
  );
};

AsnUpdatesLive.propTypes = {};

export default AsnUpdatesLive;
